import dynamic from "next/dynamic";
import { useRouter } from "next/router";

import { useMemo } from "react";

import { Loader } from "components/customElements/loader";
import { colors } from "consts";

import ToggleTimeScope from "./ToggleTimeScope";
import useFinancialData from "./useFinancialData";

const TimeSeriesChart = dynamic(
  () => import("components/AmChart/TimeSeriesChart"),
  {
    ssr: false,
  }
);

const StackedColumnChart = dynamic(
  () => import("components/AmChart/StackedColumnChart"),
  {
    ssr: false,
  }
);

const RBGChartConfig = {
  title: "Revenue By Geography",
};

const RBBSChartConfig = {
  title: "Revenue By Business Segment",
};

interface Props {
  entity: any;
}

const Financials = ({ entity }: Props) => {
  const router = useRouter();
  const source = router.query.source as string;
  const version = router.query.version as string;

  const {
    timeScopeData: chartData,
    regionalSegments,
    businessSegments,
    isLoading,
  } = useFinancialData(entity?.isin || "");

  const lastRow = useMemo(
    () => chartData?.[chartData?.length - 1],
    [chartData]
  );

  const hasRNN = useMemo(
    () => !!lastRow?.revenue || !!lastRow?.net_income || !!lastRow?.net_margin,
    [lastRow?.net_income, lastRow?.net_margin, lastRow?.revenue]
  );
  const RNNChartConfig = useMemo(
    () => ({
      title: "Revenue, Net Income & Net Margin",
      yAxisLabel: chartData?.[0]?.currency,
      charts: [
        {
          type: "column",
          name: "Revenue",
          fieldName: "revenue",
          color: colors.smartTeal,
        },
        {
          type: "column",
          name: "Net Income",
          fieldName: "net_income",
          color: colors.grass,
        },
        {
          type: "line",
          name: "Net Margin",
          fieldName: "net_margin",
          color: colors.mellowYellow,
          position: "right",
        },
      ],
    }),
    [chartData]
  );

  const hasGPE = useMemo(
    () => !!lastRow?.gross_income || !!lastRow?.ebitda,
    [lastRow?.ebitda, lastRow?.gross_income]
  );
  const GPEChartConfig = useMemo(
    () => ({
      title: "Gross Profit & EBITDA",
      yAxisLabel: chartData?.[0]?.currency,
      charts: [
        {
          type: "column",
          name: "Gross Income",
          fieldName: "gross_income",
          color: colors.smartTeal,
        },
        {
          type: "column",
          name: "EBITDA",
          fieldName: "ebitda",
          color: colors.grass,
        },
      ],
    }),
    [chartData]
  );

  const hasAL = useMemo(
    () =>
      chartData?.some(
        (item) => !!item?.assets || !!item?.liabilities || !!item?.d2a
      ),
    [chartData]
  );
  const ALChartConfig = useMemo(
    () => ({
      title: "Assets & Liabilities",
      yAxisLabel: chartData?.[0]?.currency,
      charts: [
        {
          type: "column",
          name: "Assets",
          fieldName: "assets",
          color: colors.smartTeal,
        },
        {
          type: "column",
          name: "Liabilities",
          fieldName: "liabilities",
          color: colors.grass,
        },
        {
          type: "line",
          name: "Debt to Assets",
          fieldName: "d2a",
          color: colors.mellowYellow,
          position: "right",
        },
      ],
    }),
    [chartData]
  );

  const hasCF = useMemo(
    () =>
      chartData?.some(
        (item) =>
          !!item?.operating_cf ||
          !!item?.investing_cf ||
          !!item?.fin_cf ||
          !!item?.free_cf_fcfe ||
          !!item?.fcf_yield
      ),
    [chartData]
  );
  const CFChartConfig = useMemo(
    () => ({
      title: "Cash Flow",
      yAxisLabel: chartData?.[0]?.currency,
      charts: [
        {
          type: "column",
          name: "Operating",
          fieldName: "operating_cf",
          color: colors.smartTeal,
        },
        {
          type: "column",
          name: "Investing",
          fieldName: "investing_cf",
          color: colors.grass,
        },
        {
          type: "column",
          name: "Financing",
          fieldName: "fin_cf",
          color: colors.denim,
        },
        {
          type: "column",
          name: "Free",
          fieldName: "free_cf_fcfe",
          color: colors.grass,
        },
        {
          type: "line",
          name: "FCF Yield",
          fieldName: "fcf_yield",
          color: colors.mellowYellow,
          position: "right",
        },
      ],
    }),
    [chartData]
  );

  const hasED = useMemo(
    () =>
      chartData?.some(
        (item) => !!item?.eps_dil || !!item?.div_ps || !!item?.div_yield
      ),
    [chartData]
  );
  const EDChartConfig = useMemo(
    () => ({
      title: "Earnings & Dividends",
      charts: [
        {
          type: "column",
          name: "EPS",
          fieldName: "eps_dil",
          color: colors.smartTeal,
        },
        {
          type: "column",
          name: "DPS",
          fieldName: "div_ps",
          color: colors.grass,
        },
        {
          type: "line",
          name: "Dividend Yield",
          fieldName: "div_yield",
          color: colors.mellowYellow,
          position: "right",
        },
      ],
    }),
    []
  );

  const hasCapital = useMemo(
    () =>
      chartData?.some(
        (item) =>
          !!item?.tier1_cap ||
          !!item?.tier2_cap ||
          !!item?.loan_loss_prov ||
          !!item?.income_after_llp
      ),
    [chartData]
  );
  const CapitalChartConfig = useMemo(
    () => ({
      title: "Capital",
      charts: [
        {
          type: "column",
          name: "Tier 1 Capital",
          fieldName: "tier1_cap",
          color: colors.smartTeal,
        },
        {
          type: "column",
          name: "Tier 2 Capital",
          fieldName: "tier2_cap",
          color: colors.grass,
        },
        {
          type: "column",
          name: "Loan Loss Gross",
          fieldName: "loan_loss_prov",
          color: colors.jade,
        },
        {
          type: "column",
          name: "Interest Income after LLP",
          fieldName: "income_after_llp",
          color: colors.mellowYellow,
        },
      ],
    }),
    []
  );

  const hasDeposit = useMemo(
    () => chartData?.some((item) => !!item?.deposits),
    [chartData]
  );
  const DepositChartConfig = useMemo(
    () => ({
      title: "Deposits",
      charts: [
        {
          type: "column",
          name: "Total Deposits",
          fieldName: "deposits",
          color: colors.smartTeal,
        },
      ],
    }),
    []
  );

  const hasPR = useMemo(
    () =>
      chartData?.some(
        (item) => !!item?.premium_receivable || !!item?.loss_claim_reserve
      ),
    [chartData]
  );
  const PRChartConfig = useMemo(
    () => ({
      title: "Premiums & Reserves",
      charts: [
        {
          type: "column",
          name: "Premium Receivable",
          fieldName: "premium_receivable",
          color: colors.smartTeal,
        },
        {
          type: "column",
          name: "Loss Claim Reserve",
          fieldName: "loss_claim_reserve",
          color: colors.grass,
        },
      ],
    }),
    []
  );

  if (isLoading) return <Loader />;

  const chartClass = "w-full h-[400px] mb-[80px]";
  return (
    <>
      <ToggleTimeScope />

      {hasRNN ? (
        <div className={chartClass}>
          <TimeSeriesChart
            chartData={chartData}
            chartConfig={RNNChartConfig}
            className={chartClass}
          />
        </div>
      ) : null}
      {hasGPE ? (
        <div className={chartClass}>
          <TimeSeriesChart
            chartData={chartData}
            chartConfig={GPEChartConfig}
            className={chartClass}
          />
        </div>
      ) : null}
      {hasAL ? (
        <div className={chartClass}>
          <TimeSeriesChart
            chartData={chartData}
            chartConfig={ALChartConfig}
            className={chartClass}
          />
        </div>
      ) : null}
      {hasCF ? (
        <div className={chartClass}>
          <TimeSeriesChart
            chartData={chartData}
            chartConfig={CFChartConfig}
            className={chartClass}
          />
        </div>
      ) : null}
      {hasED ? (
        <div className={chartClass}>
          <TimeSeriesChart
            chartData={chartData}
            chartConfig={EDChartConfig}
            className={chartClass}
          />
        </div>
      ) : null}
      {hasCapital ? (
        <div className={chartClass}>
          <TimeSeriesChart
            chartData={chartData}
            chartConfig={CapitalChartConfig}
            className={chartClass}
          />
        </div>
      ) : null}
      {hasDeposit ? (
        <div className={chartClass}>
          <TimeSeriesChart
            chartData={chartData}
            chartConfig={DepositChartConfig}
            className={chartClass}
          />
        </div>
      ) : null}
      {hasPR ? (
        <div className={chartClass}>
          <TimeSeriesChart
            chartData={chartData}
            chartConfig={PRChartConfig}
            className={chartClass}
          />
        </div>
      ) : null}
      {(source !== "native-app" || version === "2") &&
      !!regionalSegments?.length ? (
        <div className="h-[600px] mb-[80px]">
          <StackedColumnChart
            chartData={regionalSegments}
            chartConfig={RBGChartConfig}
            className="h-[600px] mb-[80px]"
          />
        </div>
      ) : null}
      {(source !== "native-app" || version === "2") &&
      !!businessSegments?.length ? (
        <div className="h-[600px] mb-[80px]">
          <StackedColumnChart
            chartData={businessSegments}
            chartConfig={RBBSChartConfig}
            className="h-[600px] mb-[80px]"
          />
        </div>
      ) : null}
    </>
  );
};

export default Financials;
